export const PET_IDS = Object.freeze({
  SOLIS: 'solis',
  PIP: 'pip',
  BRAMBLE: 'bramble',
  MAPLE: 'maple',
  MOCHI: 'mochi',
  BANDIT: 'bandit',
  NORI: 'nori',
  LUMI: 'lumi',
  BYTE: 'byte',
  PATCH: 'patch',
  NOVA: 'nova',
  EMBER: 'ember',
  PIXEL: 'pixel',
  BOLT: 'bolt',
  ECHO: 'echo',
  ATLAS: 'atlas',
  PIXEL_SAGE: 'pixel-sage',
  PIXEL_ROSIE: 'pixel-rosie',
  PIXEL_COBALT: 'pixel-cobalt',
  PIXEL_SUNNY: 'pixel-sunny',
  PIXEL_VIOLET: 'pixel-violet',
  PIXEL_TANGERINE: 'pixel-tangerine',
  PIXEL_MINT: 'pixel-mint',
  PIXEL_ONYX: 'pixel-onyx',
});

export const PET_THEMES = Object.freeze({
  ANIMALS: 'animals',
  ROBOTS: 'robots',
  PIXELS: 'pixels',
});

const resolveAssetModule = module => module.default || module;
const imagePromiseCache = new Map();

const loadImage = (cacheKey, loader) => {
  if (!imagePromiseCache.has(cacheKey)) {
    const promise = Promise.resolve()
      .then(loader)
      .then(resolveAssetModule)
      .catch(error => {
        imagePromiseCache.delete(cacheKey);
        throw error;
      });
    imagePromiseCache.set(cacheKey, promise);
  }
  return imagePromiseCache.get(cacheKey);
};

export const PETS = Object.freeze([
  {
    id: PET_IDS.SOLIS,
    name: 'Solis',
    species: 'Lion',
    theme: PET_THEMES.ANIMALS,
    description: 'A sunny little leader who keeps brave ideas moving.',
    image: require('@/assets/images/pets/pet-solis-lion.png'),
    hoverImage: require('@/assets/images/pets/pet-solis-lion-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-solis-lion-streaming.png'),
      require('@/assets/images/pets/pet-solis-lion-streaming-2.png'),
      require('@/assets/images/pets/pet-solis-lion-streaming-3.png'),
      require('@/assets/images/pets/pet-solis-lion-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.PIP,
    name: 'Pip',
    species: 'Frog',
    theme: PET_THEMES.ANIMALS,
    description: 'A curious explorer who is always ready for the next leap.',
    image: require('@/assets/images/pets/pet-pip-frog.png'),
    hoverImage: require('@/assets/images/pets/pet-pip-frog-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-pip-frog-streaming.png'),
      require('@/assets/images/pets/pet-pip-frog-streaming-2.png'),
      require('@/assets/images/pets/pet-pip-frog-streaming-3.png'),
      require('@/assets/images/pets/pet-pip-frog-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.BRAMBLE,
    name: 'Bramble',
    species: 'Bear',
    theme: PET_THEMES.ANIMALS,
    description: 'A cozy, steady friend for long stretches of focused work.',
    image: require('@/assets/images/pets/pet-bramble-bear.png'),
    hoverImage: require('@/assets/images/pets/pet-bramble-bear-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-bramble-bear-streaming.png'),
      require('@/assets/images/pets/pet-bramble-bear-streaming-2.png'),
      require('@/assets/images/pets/pet-bramble-bear-streaming-3.png'),
      require('@/assets/images/pets/pet-bramble-bear-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.MAPLE,
    name: 'Maple',
    species: 'Red panda',
    theme: PET_THEMES.ANIMALS,
    description: 'An adventurous sidekick with a knack for finding fresh paths.',
    image: require('@/assets/images/pets/pet-maple-red-panda.png'),
    hoverImage: require('@/assets/images/pets/pet-maple-red-panda-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-maple-red-panda-streaming.png'),
      require('@/assets/images/pets/pet-maple-red-panda-streaming-2.png'),
      require('@/assets/images/pets/pet-maple-red-panda-streaming-3.png'),
      require('@/assets/images/pets/pet-maple-red-panda-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.MOCHI,
    name: 'Mochi',
    species: 'Capybara',
    theme: PET_THEMES.ANIMALS,
    description: 'A gentle companion who brings calm to a busy workspace.',
    image: require('@/assets/images/pets/pet-mochi-capybara.png'),
    hoverImage: require('@/assets/images/pets/pet-mochi-capybara-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-mochi-capybara-streaming.png'),
      require('@/assets/images/pets/pet-mochi-capybara-streaming-2.png'),
      require('@/assets/images/pets/pet-mochi-capybara-streaming-3.png'),
      require('@/assets/images/pets/pet-mochi-capybara-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.BANDIT,
    name: 'Bandit',
    species: 'Raccoon',
    theme: PET_THEMES.ANIMALS,
    description: 'A clever helper for polishing details and uncovering surprises.',
    image: require('@/assets/images/pets/pet-bandit-raccoon.png'),
    hoverImage: require('@/assets/images/pets/pet-bandit-raccoon-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-bandit-raccoon-streaming.png'),
      require('@/assets/images/pets/pet-bandit-raccoon-streaming-2.png'),
      require('@/assets/images/pets/pet-bandit-raccoon-streaming-3.png'),
      require('@/assets/images/pets/pet-bandit-raccoon-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.NORI,
    name: 'Nori',
    species: 'Penguin',
    theme: PET_THEMES.ANIMALS,
    description: 'A bright polar pal who keeps cool while solving hard problems.',
    image: require('@/assets/images/pets/pet-nori-penguin.png'),
    hoverImage: require('@/assets/images/pets/pet-nori-penguin-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-nori-penguin-streaming.png'),
      require('@/assets/images/pets/pet-nori-penguin-streaming-2.png'),
      require('@/assets/images/pets/pet-nori-penguin-streaming-3.png'),
      require('@/assets/images/pets/pet-nori-penguin-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.LUMI,
    name: 'Lumi',
    species: 'Axolotl',
    theme: PET_THEMES.ANIMALS,
    description: 'A cheerful deep thinker who brings fresh ideas to the surface.',
    image: require('@/assets/images/pets/pet-lumi-axolotl.png'),
    hoverImage: require('@/assets/images/pets/pet-lumi-axolotl-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-lumi-axolotl-streaming.png'),
      require('@/assets/images/pets/pet-lumi-axolotl-streaming-2.png'),
      require('@/assets/images/pets/pet-lumi-axolotl-streaming-3.png'),
      require('@/assets/images/pets/pet-lumi-axolotl-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.BYTE,
    name: 'Byte',
    species: 'Utility robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A sharp little processor who turns tangled questions into clear steps.',
    image: require('@/assets/images/pets/pet-byte-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-byte-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-byte-robot-streaming.png'),
      require('@/assets/images/pets/pet-byte-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-byte-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-byte-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.PATCH,
    name: 'Patch',
    species: 'Repair robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A practical fixer who keeps projects moving when something breaks.',
    image: require('@/assets/images/pets/pet-patch-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-patch-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-patch-robot-streaming.png'),
      require('@/assets/images/pets/pet-patch-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-patch-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-patch-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.NOVA,
    name: 'Nova',
    species: 'Cosmic robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A starry-eyed thinker who finds bright possibilities in the unknown.',
    image: require('@/assets/images/pets/pet-nova-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-nova-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-nova-robot-streaming.png'),
      require('@/assets/images/pets/pet-nova-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-nova-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-nova-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.EMBER,
    name: 'Ember',
    species: 'Forge robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A warm powerhouse who brings bold energy to ambitious work.',
    image: require('@/assets/images/pets/pet-ember-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-ember-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-ember-robot-streaming.png'),
      require('@/assets/images/pets/pet-ember-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-ember-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-ember-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL,
    name: 'Pixel',
    species: 'Creative robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A playful maker who adds color and imagination to every idea.',
    image: require('@/assets/images/pets/pet-pixel-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-pixel-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-pixel-robot-streaming.png'),
      require('@/assets/images/pets/pet-pixel-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-pixel-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-pixel-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.BOLT,
    name: 'Bolt',
    species: 'Scout robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A speedy optimist who charges ahead and keeps momentum high.',
    image: require('@/assets/images/pets/pet-bolt-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-bolt-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-bolt-robot-streaming.png'),
      require('@/assets/images/pets/pet-bolt-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-bolt-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-bolt-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.ECHO,
    name: 'Echo',
    species: 'Signal robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A perceptive communicator who listens closely and keeps ideas connected.',
    image: require('@/assets/images/pets/pet-echo-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-echo-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-echo-robot-streaming.png'),
      require('@/assets/images/pets/pet-echo-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-echo-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-echo-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.ATLAS,
    name: 'Atlas',
    species: 'Explorer robot',
    theme: PET_THEMES.ROBOTS,
    description: 'A resourceful navigator who helps chart a path through the unknown.',
    image: require('@/assets/images/pets/pet-atlas-robot.png'),
    hoverImage: require('@/assets/images/pets/pet-atlas-robot-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-atlas-robot-streaming.png'),
      require('@/assets/images/pets/pet-atlas-robot-streaming-2.png'),
      require('@/assets/images/pets/pet-atlas-robot-streaming-3.png'),
      require('@/assets/images/pets/pet-atlas-robot-streaming-4.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_SAGE,
    name: 'Sage',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'A thoughtful little Pixel who enjoys finding a clear path.',
    image: require('@/assets/images/pets/pet-sage-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-sage-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-sage-pixel-working.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_ROSIE,
    name: 'Rosie',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'A warm-hearted Pixel who cheers on every small win.',
    image: require('@/assets/images/pets/pet-rosie-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-rosie-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-rosie-pixel-working.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_COBALT,
    name: 'Cobalt',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'A curious Pixel who loves puzzles and clever solutions.',
    image: require('@/assets/images/pets/pet-cobalt-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-cobalt-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-cobalt-pixel-working.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_SUNNY,
    name: 'Sunny',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'An upbeat Pixel who brings a little sunshine to your desk.',
    image: require('@/assets/images/pets/pet-sunny-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-sunny-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-sunny-pixel-working.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_VIOLET,
    name: 'Violet',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'An imaginative Pixel with a soft spot for big ideas.',
    image: require('@/assets/images/pets/pet-violet-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-violet-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-violet-pixel-working.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_TANGERINE,
    name: 'Tangerine',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'A spirited Pixel who makes getting started feel easy.',
    image: require('@/assets/images/pets/pet-tangerine-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-tangerine-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-tangerine-pixel-working.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_MINT,
    name: 'Mint',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'An easygoing Pixel who keeps busy days feeling lighter.',
    image: require('@/assets/images/pets/pet-mint-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-mint-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-mint-pixel-working.png'),
    ]),
  },
  {
    id: PET_IDS.PIXEL_ONYX,
    name: 'Onyx',
    species: 'Pixel',
    theme: PET_THEMES.PIXELS,
    description: 'A quietly clever Pixel with a dry sense of humor.',
    image: require('@/assets/images/pets/pet-onyx-pixel.png'),
    hoverImage: require('@/assets/images/pets/pet-onyx-pixel-hover.png'),
    streamingImages: Object.freeze([
      require('@/assets/images/pets/pet-onyx-pixel-working.png'),
    ]),
  },
]);

export const PET_ID_SET = new Set(PETS.map(pet => pet.id));

export const getPetById = id => PETS.find(pet => pet.id === id) || null;

export const loadPetImage = pet => (
  pet ? loadImage(`${pet.id}:default`, () => pet.image) : Promise.resolve(null)
);

export const loadPetHoverImage = pet => (
  pet ? loadImage(`${pet.id}:hover`, () => pet.hoverImage) : Promise.resolve(null)
);

export const loadPetStreamingImages = pet => (
  pet
    ? Promise.all(pet.streamingImages.map((image, index) =>
      loadImage(`${pet.id}:streaming:${index}`, () => image)
    ))
    : Promise.resolve([])
);
